import { useCallback } from 'react';
import {
  useGetSchedulingSettingsQuery,
  useUpdateSchedulingSettingsMutation,
} from '../endpoints/scheduling.endpoints';
import type { UpdateSchedulingSettingsParams } from '../types/scheduling.types';

type SaveResult = { ok: true } | { ok: false; message: string };

function toApiTime(hhmm: string): string {
  return hhmm.length === 5 ? `${hhmm}:00` : hhmm;
}

export function useSchedulingSettings() {
  const { data, isLoading, isError } = useGetSchedulingSettingsQuery();
  const [updateSettings, { isLoading: isSaving }] = useUpdateSchedulingSettingsMutation();

  const saveBookingWindow = useCallback(async(
    bookingStartTime: string,
    bookingEndTime: string
  ): Promise<SaveResult> => {
    const payload: UpdateSchedulingSettingsParams = {
      booking_start_time: toApiTime(bookingStartTime),
      booking_end_time: toApiTime(bookingEndTime),
    };

    try {
      await updateSettings(payload).unwrap();
      return { ok: true };
    } catch (error: unknown) {
      const responseData = (error as { responseData?: Record<string, unknown> })?.responseData;
      const detail = typeof responseData?.detail === 'string'
        ? responseData.detail
        : typeof responseData?.booking_end_time === 'string'
          ? responseData.booking_end_time
          : 'Failed to save booking window.';
      return { ok: false, message: detail };
    }
  }, [updateSettings]);

  return {
    settings: data,
    isLoading,
    isError,
    isSaving,
    saveBookingWindow,
  };
}
